import { memo, useEffect, useState } from "react";

import { CloseCircle } from "iconsax-react";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@zenith/assets/icons/Close";

/**
 * Props for Discard Modal Component.
 * @interface
 * @name DiscardModalComponentProps
 * @kind interface
 */
interface DiscardModalComponentProps {
  /**
   * If `true`, opens the Discard Modal.
   * @default false
   * @property
   * @name isOpen
   * @kind property
   * @memberof DiscardModalComponentProps
   * @type {boolean}
   */
  isOpen: boolean;
  /**
   * Callback fired when the modal requests to be closed.
   * @method
   * @name onClose
   * @kind property
   * @memberof DiscardModalComponentProps
   * @returns {void}
   */
  onClose: () => void;
  /**
   * Callback fired when clicked, requesting the primary action.
   * @method
   * @name primaryAction
   * @kind property
   * @memberof DiscardModalComponentProps
   * @returns {void}
   */
  primaryAction: () => void;
  /**
   * Callback fired when clicked, requesting the secondary action.
   * @method
   * @name secondaryAction
   * @kind property
   * @memberof DiscardModalComponentProps
   * @returns {void}
   */
  secondaryAction: () => void;
  /**
   * Title of the Discard Modal.
   * @property
   * @name title
   * @kind property
   * @memberof DiscardModalComponentProps
   * @type {string}
   */
  title: string;
  /**
   * Body text of the Discard Modal.
   * @property
   * @name bodyText
   * @kind property
   * @memberof DiscardModalComponentProps
   * @type {string}
   */
  bodyText: string;
  /**
   * Text of the primary action text.
   * @property
   * @name primaryActionText
   * @kind property
   * @memberof DiscardModalComponentProps
   * @type {string}
   */
  primaryActionText: string;
  /**
   * Text of the secondary action text.
   * @property
   * @name secondaryActionText
   * @kind property
   * @memberof DiscardModalComponentProps
   * @type {string}
   */
  secondaryActionText: string;
}

/**
 * Memoized Discard Modal Component.
 * @constant
 * @name DiscardModalComponent
 * @kind variable
 * @type {React.NamedExoticComponent<DiscardModalComponentProps>}
 */
const DiscardModalComponent: React.NamedExoticComponent<DiscardModalComponentProps> =
  memo(
    /**
     * Discard Modal Component.
     * @function
     * @name DiscardModalComponent
     * @kind function
     * @memberof DiscardModalComponent
     * @param {DiscardModalComponentProps} props Props for Discard Modal Component.
     * @returns {JSX.Element} Discard Modal Component.
     */
    function DiscardModalComponent(
      props: DiscardModalComponentProps
    ): JSX.Element {
      const [open, setOpen] = useState(props.isOpen);

      useEffect(() => {
        setOpen(props.isOpen);
      }, [props.isOpen]);

      return (
        <Modal open={open} onClose={() => setOpen(false)}>
          <div className="fixed top-0 left-0 flex h-full w-full items-center justify-center bg-black bg-opacity-50">
            <div className="w-100 max-w-[94%] rounded-2xl bg-white p-6 shadow">
              <div className="flex flex-col items-center justify-center">
                <IconButton
                  id="close-cart-button"
                  onClick={props.onClose}
                  className="self-end text-neutral-900"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <div className="flex flex-col items-center justify-center ">
                  <CloseCircle size="48" className="text-primary-300" />
                  <h1 className=" mt-4 text-xl font-normal text-neutral-900">
                    {props.title}
                  </h1>
                </div>
              </div>
              <p
                id="cart-discard-confirmation-text"
                className="mx-auto w-56 max-w-full pt-4 pb-12 text-center text-sm text-neutral-600"
              >
                {props.bodyText}
              </p>
              <div className="flex flex-row justify-between gap-3">
                <Button
                  id="checkout-cart-button"
                  className="w-1/2 rounded-lg border-1 border-solid border-neutral-300 py-[11px] text-xs font-normal normal-case text-neutral-600 hover:border-neutral-300 hover:bg-white md:text-base"
                  size="large"
                  onClick={props.primaryAction}
                >
                  {props.primaryActionText}
                </Button>
                <Button
                  id="cancel-cart-button"
                  className="w-1/2 rounded-lg bg-primary-300 py-[11px] text-xs font-medium normal-case text-white hover:bg-primary-300 md:text-base"
                  onClick={props.secondaryAction}
                  size="large"
                >
                  {props.secondaryActionText}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      );
    }
  );

export default DiscardModalComponent;
